import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { UserRoleProvider } from './contexts/UserRoleContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import Dashboard from './pages/Dashboard/Dashboard';
import UserManagement from './pages/UserManagement/UserManagement';
import BotConfiguration from './pages/BotConfiguration/BotConfiguration';
import KnowledgeBase from './pages/KnowledgeBase/KnowledgeBase';
import Analytics from './pages/Analytics/Analytics';
import Settings from './pages/Settings/Settings';
import Tenants from './pages/Tenants/Tenants';
import Companies from './pages/Companies/Companies';
import Bots from './pages/Bots/Bots';
import WidgetPreview from './pages/WidgetPreview/WidgetPreview';
import TenantMembers from './pages/TenantMembers/TenantMembers';
import Credentials from './pages/Credentials/Credentials';
import CompanyMembers from './pages/CompanyMembers/CompanyMembers';
import Webhooks from './pages/Webhooks/Webhooks';
import ChatSessions from './pages/ChatSessions/ChatSessions';
import ChatInterface from './pages/ChatInterface/ChatInterface';
import CompanyChat from './pages/CompanyChat/CompanyChat';
import ChatWidget from './pages/ChatWidget/ChatWidget';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

// KiChatbot Theme - Schwarz/Gelb/Weiß
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#FFD700', // Gold/Gelb
      light: '#FFF59D',
      dark: '#F57F17',
      contrastText: '#000000',
    },
    secondary: {
      main: '#000000', // Schwarz
      light: '#424242',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FAFAFA',
    },
    text: {
      primary: '#000000',
      secondary: '#424242',
    },
    error: {
      main: '#D32F2F',
    },
    warning: {
      main: '#FFA000',
    },
    success: {
      main: '#388E3C',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SocketProvider>
            <UserRoleProvider>
              <Router>
              <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  {/* Public Chat Interface - ohne Layout */}
                  <Route path="/chat/session/:sessionId" element={<ChatInterface />} />
                  <Route path="/chat-session/:companySlug/:sessionId" element={<CompanyChat />} />
                  <Route path="/chat/widget/:botId" element={<ChatWidget />} />
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute requireApproval={false}>
                        <Layout>
                          <Routes>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/tenants" element={
                              <ProtectedRoute requireApproval={true}>
                                <Tenants />
                              </ProtectedRoute>
                            } />
                            <Route path="/companies" element={
                              <ProtectedRoute requireApproval={true}>
                                <Companies />
                              </ProtectedRoute>
                            } />
                            <Route path="/bots" element={
                              <ProtectedRoute requireApproval={true}>
                                <Bots />
                              </ProtectedRoute>
                            } />
                            <Route path="/widget-preview" element={
                              <ProtectedRoute requireApproval={true}>
                                <WidgetPreview />
                              </ProtectedRoute>
                            } />
                            <Route path="/tenant-members" element={
                              <ProtectedRoute requireApproval={true}>
                                <TenantMembers />
                              </ProtectedRoute>
                            } />
                            <Route path="/credentials" element={
                              <ProtectedRoute requireApproval={true}>
                                <Credentials />
                              </ProtectedRoute>
                            } />
                            <Route path="/company-members" element={
                              <ProtectedRoute requireApproval={true}>
                                <CompanyMembers />
                              </ProtectedRoute>
                            } />
                            <Route path="/webhooks" element={
                              <ProtectedRoute requireApproval={true}>
                                <Webhooks />
                              </ProtectedRoute>
                            } />
                            <Route path="/users" element={
                              <ProtectedRoute requireApproval={true}>
                                <UserManagement />
                              </ProtectedRoute>
                            } />
                            <Route path="/bot-config" element={
                              <ProtectedRoute requireApproval={true}>
                                <BotConfiguration />
                              </ProtectedRoute>
                            } />
                            <Route path="/knowledge" element={
                              <ProtectedRoute requireApproval={true}>
                                <KnowledgeBase />
                              </ProtectedRoute>
                            } />
                            <Route path="/analytics" element={
                              <ProtectedRoute requireApproval={true}>
                                <Analytics />
                              </ProtectedRoute>
                            } />
                            <Route path="/settings" element={
                              <ProtectedRoute requireApproval={true}>
                                <Settings />
                              </ProtectedRoute>
                            } />
                            <Route path="/chat-sessions" element={
                              <ProtectedRoute requireApproval={true}>
                                <ChatSessions />
                              </ProtectedRoute>
                            } />
                          </Routes>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Box>
              </Router>
            </UserRoleProvider>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;